import { StyleSheet } from 'aphrodite';
import { type ReactNode, useId, useState } from 'react';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '../../../web/components-v2/Field';
import { SPACE } from '../../../web/components-v2/Theme/theme';
import { TextArea } from '../../../web/components-v2/TextArea';

interface IReasonFormProps {
	label: string;
	help?: string;
	confirmLabel: string;
	isPending: boolean;
	canConfirm: boolean;
	onCancel: () => void;
	onConfirm: (reason: string) => void;
	children?: ReactNode;
}

export function ReasonDialog({
	open,
	onOpenChange,
	title,
	description,
	canConfirm = true,
	...formProps
}: Omit<IReasonFormProps, 'canConfirm' | 'onCancel'> & {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description?: string;
	canConfirm?: boolean;
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					{description ? <DialogDescription>{description}</DialogDescription> : null}
				</DialogHeader>
				<ReasonForm {...formProps} canConfirm={canConfirm} onCancel={() => onOpenChange(false)} />
			</DialogPopup>
		</Dialog>
	);
}

function ReasonForm({
	label,
	help,
	confirmLabel,
	isPending,
	canConfirm,
	onCancel,
	onConfirm,
	children,
}: IReasonFormProps) {
	const reasonId = useId();
	const [reason, setReason] = useState('');
	const trimmed = reason.trim();

	return (
		<>
			<DialogBody styles={[styles.body]}>
				{children}
				<FieldGroup>
					<Field>
						<FieldLabel htmlFor={reasonId}>{label} (required)</FieldLabel>
						<TextArea id={reasonId} rows={4} value={reason} onChange={e => setReason(e.target.value)} />
						{help ? <FieldDescription>{help}</FieldDescription> : null}
					</Field>
				</FieldGroup>
			</DialogBody>
			<DialogFooter>
				<Button type='button' variant='outline' onClick={onCancel}>
					Cancel
				</Button>
				<Button
					type='button'
					variant='primary'
					disabled={!trimmed || !canConfirm || isPending}
					isLoading={isPending}
					onClick={() => onConfirm(trimmed)}
				>
					{confirmLabel}
				</Button>
			</DialogFooter>
		</>
	);
}

const styles = StyleSheet.create({
	body: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[16],
	},
});
